import { NgModule } from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
} from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MessagePopupComponent } from './modals/message-popup/message-popup.component';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { HomeComponent } from './pages/home/home.component';
import { LoginComponent } from './pages/login/login.component';
import { OrdersComponent } from './pages/dashboard/orders/orders.component';
import { RegisterComponent } from './pages/register/register.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ComponentsModule } from './components/components.module';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import { JwtInterceptor } from './interceptors/jwt.interceptor';
import { AutoLoginGuard } from './guards/auto-login.guard';
import { AuthGuard } from './guards/auth.guard';
import { CommonModule } from '@angular/common';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ProductsComponent } from './pages/dashboard/products/products.component';
import { BranchesComponent } from './pages/dashboard/branches/branches.component';
import { TeamComponent } from './pages/dashboard/team/team.component';
import { ThirdPartyComponent } from './pages/dashboard/third-party/third-party.component';
import { AnalyticsComponent } from './pages/dashboard/analytics/analytics.component';
import { SettingsComponent } from './pages/dashboard/settings/settings.component';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ChartModule } from 'primeng/chart';
import { CalendarModule } from 'primeng/calendar';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CarouselModule } from 'primeng/carousel';
import { MessageService } from 'primeng/api';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ProductPopupComponent } from './modals/product-popup/product-popup.component';
import { DeletePopupComponent } from './modals/delete-popup/delete-popup.component';
import { BranchPopupComponent } from './modals/branch-popup/branch-popup.component';
import { TeamMemberPopupComponent } from './modals/team-member-popup/team-member-popup.component';
import { ThirdPartyPopupComponent } from './modals/third-party-popup/third-party-popup.component';
import { BusinessCategoryPopupComponent } from './modals/business-category-popup/business-category-popup.component';
import { ResetPasswordComponent } from './modals/reset-password/reset-password.component';
import { DeleteAccountComponent } from './modals/delete-account/delete-account.component';
import { QRCodeComponent } from 'angularx-qrcode';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { PipesModule } from './pipes/pipes.module';
import { ChangePasswordComponent } from './pages/change-password/change-password.component';
import { VerifyComponent } from './pages/verify/verify.component';
import { ProductDetailsComponent } from './modals/product-details/product-details.component';
import { DiscoverComponent } from './pages/discover/discover.component';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { PrivacyPolicyComponent } from './pages/privacy-policy/privacy-policy.component';
import { TermsOfServiceComponent } from './pages/terms-of-service/terms-of-service.component';
import { BusinessesComponent } from './pages/dashboard/businesses/businesses.component';
import { BusinessPopupComponent } from './modals/business-popup/business-popup.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { LocationSearchPopupComponent } from './modals/location-search-popup/location-search-popup.component';
import { TablesComponent } from './pages/dashboard/tables/tables.component';
import { TablePopupComponent } from './modals/table-popup/table-popup.component';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    MessagePopupComponent,
    ForgotPasswordComponent,
    HomeComponent,
    LoginComponent,
    OrdersComponent,
    RegisterComponent,
    ProductsComponent,
    BranchesComponent,
    TeamComponent,
    ThirdPartyComponent,
    AnalyticsComponent,
    SettingsComponent,
    ProductPopupComponent,
    DeletePopupComponent,
    BranchPopupComponent,
    TeamMemberPopupComponent,
    ThirdPartyPopupComponent,
    BusinessCategoryPopupComponent,
    ResetPasswordComponent,
    DeleteAccountComponent,
    ChangePasswordComponent,
    VerifyComponent,
    ProductDetailsComponent,
    DiscoverComponent,
    PrivacyPolicyComponent,
    TermsOfServiceComponent,
    BusinessesComponent,
    BusinessPopupComponent,
    NotFoundComponent,
    LocationSearchPopupComponent,
    TablesComponent,
    TablePopupComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'en',
    }),
    FormsModule,
    CommonModule,
    HttpClientModule,
    ComponentsModule,
    DropdownModule,
    MultiSelectModule,
    TableModule,
    ToastModule,
    BrowserAnimationsModule,
    DragDropModule,
    MatCardModule,
    MatButtonModule,
    ReactiveFormsModule,
    QRCodeComponent,
    PipesModule,
    ChartModule,
    CalendarModule,
    SelectButtonModule,
    CarouselModule,
  ],
  providers: [
    provideClientHydration(),
    BsModalService,
    AutoLoginGuard,
    AuthGuard,
    {
      provide: MessageService,
      useValue: new MessageService(),
    },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: false || 'none',
        },
      },
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
